import { BillCreatedEvent } from '@rematter/frontend-lib-api-client/grpc/finance/v1/bill_events_pb';
import { BillStatus } from '@rematter/frontend-lib-api-client/grpc/finance/v1/domain_pb';
import {
  BadgeField,
  Col,
  FieldDisplay,
  humanReadableDateTime,
  ThemeColor,
} from '@rematter/ui-web';

import {
  billStatusLabels,
  billTypeLabels,
  paymentMethodLabels,
  paymentTermLabels,
} from '../enumLabels';

import { protoTimestampToZonedDateTimeString } from '@/audit-log/_components/_shared';
import { useTranslations } from '@/finance/i18n/useTranslations';
import { getLocale } from '~/shared/utils/locale';

const getStatusColor = (status: BillStatus): ThemeColor => {
  switch (status) {
    case BillStatus.PENDING:
      return 'warning';
    case BillStatus.PAID:
      return 'success';
    case BillStatus.VOIDED:
      return 'failure';
    default:
      return 'neutral';
  }
};

type BillCreatedDetailProps = {
  event: BillCreatedEvent;
};

export const BillCreatedDetail = ({ event }: BillCreatedDetailProps) => {
  const t = useTranslations();
  const bill = event.bill;

  if (!bill) {
    return (
      <Col className="@BillCreatedDetail items-center justify-center p-4 text-neutral/40">
        <span>{t.logs.noDataAvailable}</span>
      </Col>
    );
  }

  const documentDateStr = protoTimestampToZonedDateTimeString(
    bill.documentDate,
  );
  const eligibleAtStr = protoTimestampToZonedDateTimeString(bill.eligibleAt);

  const formatAmount = () => {
    if (!bill.amount) return 'N/A';
    const value = Number(bill.amount.amount) / 100;
    return `${bill.amount.currency} ${value.toFixed(2)}`;
  };

  return (
    <Col className="@BillCreatedDetail gap-3" gap3>
      <Col className="rounded-md border border-neutral/10 bg-white p-3" gap2>
        <span className="text-xs font-medium text-neutral/60">
          {t.logs.billDetails}
        </span>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <FieldDisplay label="Document Number" value={bill.documentNumber} />
          <BadgeField
            label="Status"
            value={billStatusLabels[bill.status]}
            color={getStatusColor(bill.status)}
          />
          <FieldDisplay label="Type" value={billTypeLabels[bill.type]} />
          <FieldDisplay label="Amount" value={formatAmount()} />
          {documentDateStr && (
            <FieldDisplay
              label="Document Date"
              value={humanReadableDateTime(documentDateStr, {
                locale: getLocale(),
              })}
            />
          )}
          {eligibleAtStr && (
            <FieldDisplay
              label="Eligible At"
              value={humanReadableDateTime(eligibleAtStr, {
                locale: getLocale(),
              })}
            />
          )}
          <FieldDisplay
            label="Payment Term"
            value={paymentTermLabels[bill.paymentTerm]}
          />
          <FieldDisplay
            label="Payment Method"
            value={paymentMethodLabels[bill.preferredPaymentMethod]}
          />
          <FieldDisplay label="Facility ID" value={bill.facilityId?.value} />
          <FieldDisplay label="Account ID" value={bill.accountId?.value} />
          {bill.notes && (
            <FieldDisplay
              label="Notes"
              value={bill.notes}
              className="col-span-2"
            />
          )}
        </div>
      </Col>
    </Col>
  );
};
